import React from 'react';
import { FaExternalLinkAlt, FaFacebookF, FaHandshake, FaAward, FaUsers, FaGraduationCap, FaCarSide, FaGlobe } from 'react-icons/fa';

const PartnersSection = () => {
  const partners = [
    {
      name: 'LK Powercell Sdn Bhd',
      logo: 'https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/2df7lufi_LOGO%20HYbrid%20PNG.png',
      category: 'Hybrid Battery Technology Partner',
      description: 'Specializes in hybrid battery diagnostics, repair, refurbishment and replacement for Toyota, Lexus and other hybrid vehicles. Advanced hybrid battery solutions and technical support you can rely on.',
      website: null,
      facebook: null,
      // Blue brand
      accentColor: '#0066CC',
      accentBg: 'bg-blue-50',
      accentText: 'text-blue-700',
      logoBg: 'bg-gradient-to-br from-blue-50 via-white to-blue-50',
      buttonBg: 'bg-[#0066CC] hover:bg-[#004C99]'
    },
    {
      name: 'Leng Kee Auto Academy Sdn Bhd',
      logo: 'https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/01msxref_leng%20kee%20academy%20logo.jpg',
      category: 'Automotive Technical Training Partner',
      description: 'Professional automotive training academy dedicated to developing highly skilled technicians through industry-focused training, practical workshops and competency-based education programs.',
      website: 'https://lengkeeauto.edu.my/',
      facebook: 'https://www.facebook.com/lengkeeacademy',
      // Amber/Orange brand
      accentColor: '#F59E0B',
      accentBg: 'bg-amber-50',
      accentText: 'text-amber-700',
      logoBg: 'bg-gradient-to-br from-amber-50 via-white to-stone-50',
      buttonBg: 'bg-[#F59E0B] hover:bg-[#D97706]'
    },
    {
      name: 'Divo Academy',
      logo: 'https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/qhwg8ybl_Divo%20logo.jpg',
      category: 'Education & Professional Development Partner',
      description: 'Professional education and lifelong learning programs designed to develop talent, leadership and technical competencies across various industries.',
      website: 'https://www.divo.edu.my/',
      facebook: 'https://www.facebook.com/divoacademy',
      // Teal/Cyan brand
      accentColor: '#0891B2',
      accentBg: 'bg-cyan-50',
      accentText: 'text-cyan-700',
      logoBg: 'bg-gradient-to-br from-cyan-50 via-white to-sky-50',
      buttonBg: 'bg-[#0891B2] hover:bg-[#0E7490]'
    },
    {
      name: 'EMG Skill Tech Sdn Bhd',
      logo: 'https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/guv4pgtv_EMG.png',
      category: 'Professional Mentorship & Skills Development Partner',
      description: 'Proudly established by Leng Kee Auto Academy Sdn Bhd in 2022 under the Excellent Mentor Group (EMG) brand. Mentors are competent and certified from government agencies.',
      website: null,
      facebook: 'https://www.facebook.com/ExcellentMentorGroup',
      // Gold/Yellow brand
      accentColor: '#CA8A04',
      accentBg: 'bg-yellow-50',
      accentText: 'text-yellow-700',
      logoBg: 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
      buttonBg: 'bg-[#CA8A04] hover:bg-[#A16207]'
    }
  ];

  const valueBar = [
    { icon: <FaHandshake size={22} />, label: 'Trusted Partnerships' },
    { icon: <FaAward size={22} />, label: 'Professional Expertise' },
    { icon: <FaUsers size={22} />, label: 'Industry Collaboration' },
    { icon: <FaGraduationCap size={22} />, label: 'Education & Development' },
    { icon: <FaCarSide size={22} />, label: 'Better Service For You' }
  ];

  return (
    <section className="relative py-24 bg-gradient-to-b from-white via-slate-50 to-white overflow-hidden" data-testid="partners-section">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 25% 25%, #0033A0 1px, transparent 1px), radial-gradient(circle at 75% 75%, #0033A0 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }}></div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          {/* Eyebrow with decorative lines */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-royal-blue/40"></div>
            <span className="text-royal-blue font-semibold text-xs tracking-[0.25em] uppercase">
              Our Strategic Partners
            </span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-royal-blue/40"></div>
          </div>

          {/* Main Heading */}
          <h2 className="font-cabinet font-extrabold text-3xl sm:text-4xl lg:text-5xl text-dark-navy mb-6 tracking-tight leading-[1.15] max-w-4xl mx-auto">
            Building Stronger Automotive,<br className="hidden md:block" /> Hybrid Technology and Education<br className="hidden md:block" /> Ecosystems <span className="text-royal-blue italic">Together.</span>
          </h2>

          <p className="text-gray-600 text-base max-w-3xl mx-auto leading-relaxed">
            Leng Kee Auto Service Centre Sdn Bhd works closely with trusted automotive, hybrid technology 
            and education partners to provide professional vehicle servicing, technical training and industry 
            development solutions.
          </p>
        </div>

        {/* Partners Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7 mb-16">
          {partners.map((partner, index) => (
            <div
              key={index}
              data-testid={`partner-${index}`}
              className="group relative bg-white rounded-3xl overflow-hidden shadow-[0_4px_20px_rgb(0,0,0,0.04)] hover:shadow-[0_24px_60px_rgb(0,51,160,0.12)] transition-all duration-500 hover:-translate-y-2 border border-gray-100 flex flex-col"
            >
              {/* Top accent line */}
              <div 
                className="absolute top-0 left-0 right-0 h-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: `linear-gradient(90deg, transparent, ${partner.accentColor}, transparent)` }}
              ></div>

              {/* Logo Area with brand-tinted gradient */}
              <div className={`relative ${partner.logoBg} h-48 flex items-center justify-center p-8 overflow-hidden`}>
                {/* Decorative corner accent */}
                <div 
                  className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-10 transition-transform duration-700 group-hover:scale-150"
                  style={{ backgroundColor: partner.accentColor }}
                ></div>
                <div 
                  className="absolute -bottom-8 -left-8 w-20 h-20 rounded-full opacity-10 transition-transform duration-700 group-hover:scale-150"
                  style={{ backgroundColor: partner.accentColor }}
                ></div>

                <a
                  href={partner.website || partner.facebook || '#'}
                  target={partner.website || partner.facebook ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  className="relative z-10 w-full h-full flex items-center justify-center"
                  aria-label={`${partner.name} logo`}
                >
                  <img
                    src={partner.logo}
                    alt={`${partner.name} Logo`}
                    className="max-h-32 max-w-[80%] w-auto h-auto object-contain transition-transform duration-500 group-hover:scale-110"
                  />
                </a>
              </div>

              {/* Card Content */}
              <div className="px-7 pt-6 pb-7 flex-1 flex flex-col">
                {/* Partner Name */}
                <h3 className="font-cabinet font-bold text-lg text-dark-navy mb-3 leading-snug text-center">
                  {partner.name}
                </h3>

                {/* Category Badge */}
                <div className="flex justify-center mb-4">
                  <div 
                    className={`inline-block px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${partner.accentBg} ${partner.accentText}`}
                  >
                    {partner.category}
                  </div>
                </div>

                {/* Accent Divider */}
                <div className="flex justify-center mb-5">
                  <div 
                    className="h-[2px] w-16 rounded-full transition-all duration-500 group-hover:w-24"
                    style={{ backgroundColor: partner.accentColor }}
                  ></div>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 leading-relaxed mb-6 text-center flex-1">
                  {partner.description}
                </p>

                {/* Action Buttons */}
                <div className="space-y-2 mt-auto">
                  {partner.website && partner.facebook && (
                    <div className="grid grid-cols-2 gap-2">
                      <a
                        href={partner.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`partner-${index}-website`}
                        className={`flex items-center justify-center gap-1.5 px-3 py-2.5 ${partner.buttonBg} text-white text-xs font-bold uppercase tracking-wide rounded-full transition-all hover:shadow-lg`}
                      >
                        <FaGlobe size={11} />
                        Website
                      </a>
                      <a
                        href={partner.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`partner-${index}-facebook`}
                        className="flex items-center justify-center gap-1.5 px-3 py-2.5 border-2 border-gray-200 text-dark-navy text-xs font-bold uppercase tracking-wide rounded-full hover:border-royal-blue hover:text-royal-blue transition-all"
                      >
                        <FaFacebookF size={11} />
                        Facebook
                      </a>
                    </div>
                  )}
                  {!partner.website && partner.facebook && (
                    <a
                      href={partner.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`partner-${index}-facebook`}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 rounded-full text-xs font-bold uppercase tracking-wide transition-all"
                      style={{ borderColor: partner.accentColor, color: partner.accentColor }}
                    >
                      <FaFacebookF size={12} />
                      Follow on Facebook
                    </a>
                  )}
                  {!partner.website && !partner.facebook && (
                    <div className="text-center text-xs text-gray-400 italic py-2.5 px-4 bg-gray-50 rounded-full">
                      Exclusive Strategic Partner
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Value Bar - 5 key benefits */}
        <div className="bg-white rounded-3xl shadow-[0_4px_30px_rgb(0,0,0,0.05)] border border-gray-100 p-6 lg:p-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {valueBar.map((item, index) => (
              <div
                key={index}
                data-testid={`value-${index}`}
                className="flex items-center gap-3 justify-center md:justify-start group cursor-default"
              >
                <div className="w-12 h-12 rounded-full bg-royal-blue/5 flex items-center justify-center text-royal-blue group-hover:bg-royal-blue group-hover:text-white transition-all duration-300 flex-shrink-0">
                  {item.icon}
                </div>
                <span className="font-cabinet font-bold text-sm text-dark-navy leading-tight">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnersSection;
